import { publicPageProperties } from '../../shared/analytics-safety';
export interface PostHogEvent {
  event: string;
  properties?: Record<string, unknown>;
  $set?: Record<string, unknown>;
  $set_once?: Record<string, unknown>;
}

const sensitivePropertyPattern = /(^|_)(url|href|host|path|pathname|query|search|referrer|referring_domain|device_code|share_slug|recording_id)($|_)/;

export const POSTHOG_PRIVACY_CONFIG = {
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  capture_exceptions: false,
  capture_heatmaps: false,
  capture_performance: { web_vitals: true, web_vitals_attribution: false },
  disable_session_recording: true,
  enable_recording_console_log: false,
  session_recording: {
    maskAllInputs: true,
    maskTextSelector: '[contenteditable], .run-url-summary, .run-task-summary',
    blockSelector: '.app-frame, iframe, canvas',
    recordCrossOriginIframes: false,
    recordHeaders: false,
    recordBody: false,
    captureCanvas: { recordCanvas: false },
    captureJsonLd: false,
    maskCapturedNetworkRequestFn: () => null,
  },
  disable_surveys: true,
  advanced_disable_flags: true,
  save_referrer: false,
  save_campaign_params: false,
  before_send: sanitizePostHogEvent,
} as const;

export function sanitizePostHogEvent<T extends PostHogEvent>(event: T | null): T | null {
  if (!event || (typeof navigator !== 'undefined' && (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl)) return null;
  if (event.event === '$web_vitals') {
    if (typeof navigator !== 'undefined' && (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl) return null;
    // Use the metric's buffered URL, not the current SPA route. Keep only
    // public page dimensions and numeric scores; raw entries can contain DOM data.
    let page;
    try {
      const url = new URL(String(event.properties?.$current_url));
      if (url.origin !== 'https://userill.dev') return null;
      page = publicPageProperties(url.pathname, '');
    } catch { return null; }
    if (!page) return null;
    const properties = Object.fromEntries(Object.entries(event.properties ?? {}).filter(([key, value]) =>
      !key.startsWith('$web_vitals_') || (/^\$web_vitals_(LCP|CLS|FCP|INP)_value$/.test(key) && typeof value === 'number' && Number.isFinite(value))));
    return { ...event, properties: { ...sanitizeProperties(properties), ...page } } as T;
  }
  // Only our manually emitted public page events may carry URL dimensions.
  const page = (event.event === '$pageview' || event.event === '$pageleave') && event.properties?.public_page === true
    && typeof event.properties.$pathname === 'string'
    ? publicPageProperties(event.properties.$pathname, typeof event.properties.$referrer === 'string' ? event.properties.$referrer : '') : null;
  if ((event.event === '$pageview' || event.event === '$pageleave') && !page) return null;
  return {
    ...event,
    ...(event.properties ? { properties: { ...sanitizeProperties(event.properties), ...(page ?? {}) } } : {}),
    ...(event.$set ? { $set: sanitizeProperties(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: sanitizeProperties(event.$set_once) } : {}),
  } as T;
}

function sanitizeProperties(properties: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties)
      .filter(([key]) => !sensitivePropertyPattern.test(key.replace(/^\$+/, '').toLowerCase()))
      .map(([key, value]) => [key, sanitizeValue(value)]),
  );
}

function sanitizeValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sanitizeValue);
  if (value && typeof value === 'object') return sanitizeProperties(value as Record<string, unknown>);
  return value;
}
